import styled from '@emotion/styled'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocale } from '@/hooks/useLocale'
import { colors } from '@/styles/theme'
import { ImagesSelector } from '@/components/molecules/ImagesSelector'
import { ColorSelector } from '@/components/atoms/ColorSelector'
import { PlatformSelector } from '@/components/atoms/PlatformSelector'
import { SexSelector } from '@/components/atoms/SexSelector'
import { VoiceChatSelector } from '@/components/atoms/VoiceChatSelector'
import {
  InputText,
  InputSelector,
  OptionsPlaytime,
} from '../../../atoms/Inputs'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { ImageSelector } from '@/components/atoms/ImageSelector'
import { TemplateSelector } from '@/components/atoms/TemplateSelector'
import { format } from 'date-fns'
import { ProfileCardEditorDescription } from '@/components/atoms/ProfileCardEditorDescription'
import {
  CREATOR,
  creatorMapTargetId,
  CREATORS,
  mapBF2042CreatorInfo,
} from './config'
import { BF2042_1 } from './BF2042_1'

type FormField = {
  id: string
  name: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  playTime: string
  playStyle: string
  weapon: string
  vehicle: string
  hitokoto: string
}

type Props = {
  onCreate: (
    data: FormField & { selectedLegends: number[] },
    targetId: string,
    selectedSex: Sex,
    selectedPlatform: Array<Platform>,
    selectedVoiceChat: Array<VoiceChat>
  ) => void
  submitButtonDisable: boolean
  fontColors: string[]
  fonts: { imagePath: string; value: string }[]
  creatorParam: string | null
}

const creatorMapColors: Record<CREATOR, string[]> = {
  uni_3221: ['#FEBF64'],
}

const creatorMapLegends: Record<CREATOR, string[]> = {
  uni_3221: Array(14)
    .fill(0)
    .map((_, index) => {
      return `/static/images/characters/bf2042_1/${index}.png`
    }),
}

const templates = Object.keys(mapBF2042CreatorInfo).map((creatorName) => {
  return {
    imagePath: mapBF2042CreatorInfo[creatorName]['profileCardImageURL'],
    value: creatorName,
  }
})

export const EditorBF2042: React.FC<Props> = ({
  onCreate,
  submitButtonDisable,
  fontColors,
  fonts,
  creatorParam,
}) => {
  const { t } = useLocale()
  const { handleSubmit, register, watch } = useForm<FormField>({
    defaultValues: {
      playTimeWeekDayBegin: '18',
      playTimeWeekDayEnd: '19',
      playTimeWeekEndBegin: '18',
      playTimeWeekEndEnd: '19',
    },
  })
  const fieldVal = watch()

  const [selectedCreator, setSelectedCreator] = useState<CREATOR>(
    CREATORS.includes(creatorParam as any)
      ? (creatorParam as CREATOR)
      : CREATORS[0]
  )

  useEffect(() => {
    if (!selectedCreator) return
    const url = new URL(window.location.href)
    url.searchParams.set('creator', selectedCreator)
    window.history.pushState(null, '', url.toString())
  }, [selectedCreator])

  const onChangeCreator = useCallback((creator: CREATOR) => {
    setSelectedCreator(creator)
  }, [])

  const [selectedFontFamily, setSelectedFontFamily] = useState('ap')
  const [selectedColor, setColor] = useState(0)
  const onChangeColor = useCallback((selectedIndex: number) => {
    setColor(selectedIndex)
  }, [])
  const [selectedFontColor, setFontColor] = useState(0)
  const onChangeFontColor = useCallback((selectedIndex: number) => {
    setFontColor(selectedIndex)
  }, [])
  const [selectedSex, setSex] = useState<Sex>(0)
  const onChangeSex = useCallback((selectedIndex: Sex) => {
    setSex(selectedIndex)
  }, [])

  const [selectedPlatform, setPlatform] = useState<Array<Platform>>([0])
  const onChangePlatform = useCallback((selectedIndex: Array<Platform>) => {
    setPlatform(selectedIndex)
  }, [])

  const [selectedVoiceChat, setVoiceChat] = useState<Array<VoiceChat>>([])
  const onChangeVoiceChat = useCallback((selectedIndex: Array<VoiceChat>) => {
    setVoiceChat(selectedIndex)
  }, [])

  const [selectedLegends, setLegends] = useState<number[]>([])
  const onChangeLegends = useCallback((selectedIndex: number[]) => {
    setLegends(selectedIndex)
  }, [])

  const handleCreate = (data: FormField) => {
    onCreate(
      { ...data, selectedLegends },
      creatorMapTargetId[selectedCreator],
      selectedSex,
      selectedPlatform,
      selectedVoiceChat
    )
  }

  const lastModText = format(new Date(), 'yyyy年MM月dd日')
  return (
    <>
      <PreviewContainer>
        <PreviewCardContainer>
          {selectedCreator === 'uni_3221' && (
            <BF2042_1
              targetId={creatorMapTargetId[selectedCreator]}
              uid={fieldVal.id}
              fontFamily={selectedFontFamily}
              fontColor={fontColors[selectedFontColor]}
              platform={selectedPlatform}
              name={fieldVal.name}
              sex={selectedSex}
              templateColor={selectedColor as 0 | 1 | 2 | 3}
              voiceChat={selectedVoiceChat}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              playTime={fieldVal.playTime}
              playStyle={fieldVal.playStyle}
              weapon={fieldVal.weapon}
              vehicle={fieldVal.vehicle}
              hitokoto={fieldVal.hitokoto}
              characters={selectedLegends}
            />
          )}
        </PreviewCardContainer>
      </PreviewContainer>
      <DescriptionSection>
        <Time>
          <time dateTime={new Date().toISOString()} itemProp="datepublished" />
          <time dateTime={new Date().toISOString()} itemProp="modified">
            {t.最終更新}: {lastModText}
          </time>
        </Time>
        <H1Text>{t.BF2042かわいい自己紹介カード作成ツール}</H1Text>
        <H2Text>{t.BF2042かわいい自己紹介カード作成ツール説明文}</H2Text>
        <ProfileCardEditorDescription />
      </DescriptionSection>
      <div>
        <FormContainer>
          <form
            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
            onSubmit={handleSubmit(handleCreate)}
          >
            <FormItemContainer>
              <VerticalContainer>
                <SectionText>{t.テンプレートを選ぶ}</SectionText>
                <TemplateSelector
                  values={templates}
                  onChange={onChangeCreator}
                  value={selectedCreator}
                />
              </VerticalContainer>
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.カードの背景色を選ぶ}</SectionText>
              <ColorSelector
                // templateで可変
                colors={creatorMapColors[selectedCreator]}
                value={selectedColor}
                onChange={onChangeColor}
              />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.文字色を選ぶ}</SectionText>
              <ColorSelector
                colors={fontColors}
                value={selectedFontColor}
                onChange={onChangeFontColor}
              />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.フォントを選ぶ}</SectionText>
              <ImageSelector
                values={fonts}
                onChange={setSelectedFontFamily as any}
                value={selectedFontFamily}
              />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.なまえ}</SectionText>
              <InputText {...register('name', { maxLength: 20 })} />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.ID}</SectionText>
              <InputText {...register('id', { maxLength: 20 })} />
            </FormItemContainer>

            <FormItemContainer>
              <SectionText>{t.性別}</SectionText>
              <SexSelector val={selectedSex} onChange={onChangeSex} />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.プラットフォーム}</SectionText>
              <PlatformSelector
                onChange={onChangePlatform}
                value={selectedPlatform}
                activeValues={[1, 3, 2, 0]}
              />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.ボイスチャット}</SectionText>
              <VoiceChatSelector
                onChange={onChangeVoiceChat}
                value={selectedVoiceChat}
                activeValue={[0, 1, 2, 3, 4]}
              />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.プレイ時間帯平日}</SectionText>
              <InputSelector {...register('playTimeWeekDayBegin')}>
                <OptionsPlaytime />
              </InputSelector>
              <TimeSelectorText>{t.から}</TimeSelectorText>
              <InputSelector {...register('playTimeWeekDayEnd')}>
                <OptionsPlaytime />
              </InputSelector>
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.プレイ時間帯土日}</SectionText>
              <InputSelector {...register('playTimeWeekEndBegin')}>
                <OptionsPlaytime />
              </InputSelector>
              <TimeSelectorText>{t.から}</TimeSelectorText>
              <InputSelector {...register('playTimeWeekEndEnd')}>
                <OptionsPlaytime />
              </InputSelector>
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.プレイ時間帯自由記述}</SectionText>
              <InputText {...register('playTime', { maxLength: 100 })} />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.プレイスタイル}</SectionText>
              <InputText {...register('playStyle', { maxLength: 100 })} />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{'よく使うスペシャリスト'}</SectionText>
              <ImagesSelector
                values={creatorMapLegends[selectedCreator]}
                onChange={onChangeLegends}
              />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{'好きな武器'}</SectionText>
              <InputText {...register('weapon', { maxLength: 30 })} />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{'好きな乗り物'}</SectionText>
              <InputText {...register('vehicle', { maxLength: 30 })} />
            </FormItemContainer>
            <FormItemContainer>
              <SectionText>{t.ひとこと}</SectionText>
              <InputText {...register('hitokoto', { maxLength: 50 })} />
            </FormItemContainer>
            <ButtonDescriptionText>
              {t.カードの作成にはSNS連携が必要です次の画面でログインをします}
            </ButtonDescriptionText>
            <Submit
              id="create_profile"
              type="submit"
              value={t.これでカードをつくる}
              disabled={submitButtonDisable}
            />
          </form>
        </FormContainer>
      </div>
    </>
  )
}

const PreviewContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
`

const PreviewCardContainer = styled.div`
  background-color: ${colors.bg.primary};
  box-shadow: 0 0 4px rgba(32, 32, 32, 0.1);
`

const FormContainer = styled.div`
  padding: 20px;
`

const FormItemContainer = styled.div`
  margin-bottom: 32px;
`

const DescriptionSection = styled.div`
  margin-top: 16px;
  margin-left: 16px;
  margin-right: 16px;
`

const Time = styled.div`
  color: ${colors.text.secondary};
  font-size: 12px;
`

const H1Text = styled.h1`
  margin-top: 12px;
  font-size: 11px;
  font-weight: bold;
  line-height: 20px;
  color: ${colors.text.primary};
  text-align: center;
  display: flex;
`

const H2Text = styled.h2`
  margin-top: 20px;
  font-size: 11px;
  color: ${colors.text.secondary};
  line-height: 18px;
  display: flex;
`

const SectionText = styled.div`
  margin-top: 20px;
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 18px;
  color: ${colors.text.primary};
  display: flex;
`

const TimeSelectorText = styled.div`
  margin: 8px auto;
  color: ${colors.text.primary};
  font-size: 12px;
  font-weight: bold;
`

const ButtonDescriptionText = styled.h3`
  margin: auto;
  margin-top: 30px;
  width: 100%;
  font-size: 10px;
  color: ${colors.text.secondary};
  justify-content: center;
  display: flex;
`

const Submit = styled.input`
  margin-top: 12px;
  border-radius: 20px;
  border: 3px solid transparent;
  width: 100%;
  height: 40px;
  background-color: ${colors.button.primary};
  color: #fff;
  font-weight: bold;
  display: block;
`

const VerticalContainer = styled.div`
  display: flex;
  flex-flow: column;
`
